import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { Markup, PageScale, UnitSystem } from '@/types';
import { browserDefaultUnitSystem } from '@/core/units';
import { applyMarkupWrite, restoredMarkup } from '@/core/markups/revision';
import { orderTray, placedAssets, type TrayEntry, type TrayStore } from '@/core/image/tray';
import { createLogger } from '@/utils/logger';
import { pdfCache, setPdfCacheProtectedKeyResolver } from './PdfCacheService';

const log = createLogger('LocalWorkspaceService');

export interface LocalWorkspace {
  id: 'current';
  pdfBlobKey: string;
  fileName: string;
  pageCount: number;
  unitSystem: UnitSystem;
  lastModifiedAt: number;
  // Persisted dirty bit: true when the user has made edits not yet exported.
  // False for a freshly-opened PDF whose markups were imported from the source file
  // (those annotations already live in the user's file -- no warning warranted).
  hasUnsavedUserMarkups: boolean;
  /**
   * The unplaced tray (image markup spec flow 5(f), slice I8): photos added now and placed later. Their bytes are in
   * the AssetStore; these are the items. Absent = empty. Cleared with the workspace, like its markups.
   */
  tray?: TrayEntry[];
}

interface LocalWorkspaceDB extends DBSchema {
  workspace: {
    key: 'current';
    value: LocalWorkspace;
  };
  markups: {
    key: string;
    value: Markup;
    indexes: { byPage: number };
  };
  pageScales: {
    key: number;
    value: PageScale;
  };
}

/**
 * IndexedDB-based local workspace service for offline/guest mode.
 *
 * Stores:
 * - Single workspace metadata (PDF reference, page count, unit system)
 * - Markups for all pages
 * - Page scales/calibrations
 *
 * Cleanup policy: 7-day expiration from lastModifiedAt, never for a workspace holding photos (isExpired)
 */
class LocalWorkspaceService {
  private db: IDBPDatabase<LocalWorkspaceDB> | null = null;
  private readonly EXPIRATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

  // Change notification. Every useLocalMarkups() call keeps its own React state,
  // but they all read the SAME IndexedDB. Without a shared signal, a markup
  // created into the page-scoped instance is invisible to the all-document
  // instance (which loads only on mount) -- so export, which reads
  // allDocumentMarkups, silently ships zero markups. Any markup mutation fires
  // notify() so every subscribed instance re-reads from the DB and stays in sync.
  private subscribers = new Set<() => void>();
  private traySubscribers = new Set<() => void>();

  subscribe(cb: () => void): () => void {
    this.subscribers.add(cb);
    return () => {
      this.subscribers.delete(cb);
    };
  }

  private notify(): void {
    this.subscribers.forEach((cb) => {
      try {
        cb();
      } catch (error) {
        log.error('Error in markup-store subscriber:', error);
      }
    });
  }

  async init() {
    if (this.db) return;

    this.db = await openDB<LocalWorkspaceDB>('pdf-markup-local-workspace', 1, {
      upgrade(db) {
        db.createObjectStore('workspace');

        const markupStore = db.createObjectStore('markups', { keyPath: 'id' });
        markupStore.createIndex('byPage', 'pageNumber');

        db.createObjectStore('pageScales', { keyPath: 'pageNumber' });
      },
    });

    log.info('LocalWorkspaceService initialized');
  }

  // ==================== Workspace ====================

  async getWorkspace(): Promise<LocalWorkspace | null> {
    if (!this.db) await this.init();

    try {
      const workspace = await this.db!.get('workspace', 'current');
      return workspace || null;
    } catch (error) {
      log.error('Error getting workspace:', error);
      return null;
    }
  }

  async createWorkspace(
    pdfBlobKey: string,
    fileName: string,
    pageCount: number,
    unitSystem: UnitSystem = browserDefaultUnitSystem()
  ): Promise<LocalWorkspace> {
    if (!this.db) await this.init();

    const workspace: LocalWorkspace = {
      id: 'current',
      pdfBlobKey,
      fileName,
      pageCount,
      unitSystem,
      lastModifiedAt: Date.now(),
      hasUnsavedUserMarkups: false,
    };

    await this.db!.put('workspace', workspace, 'current');
    log.info(`Created workspace for ${fileName} (${pageCount} pages)`);
    return workspace;
  }

  async updateWorkspace(updates: Partial<Omit<LocalWorkspace, 'id'>>): Promise<void> {
    if (!this.db) await this.init();

    // One read-modify-write transaction: a concurrent write of another field (the tray, the dirty bit, a touch)
    // is never overwritten with a stale copy.
    const tx = this.db!.transaction('workspace', 'readwrite');
    const existing = await tx.store.get('current');
    if (!existing) {
      tx.abort();
      await tx.done.catch(() => undefined);
      throw new Error('No workspace to update');
    }
    await tx.store.put({ ...existing, ...updates, lastModifiedAt: Date.now() }, 'current');
    await tx.done;
    log.info('Updated workspace');
  }

  async setHasUnsavedUserMarkups(value: boolean): Promise<void> {
    try {
      await this.updateWorkspace({ hasUnsavedUserMarkups: value });
    } catch {
      // Workspace may not exist (cleared between edits); swallow silently.
    }
  }

  async clearWorkspace(): Promise<void> {
    if (!this.db) await this.init();

    await this.db!.clear('workspace');
    await this.db!.clear('markups');
    await this.db!.clear('pageScales');
    this.notify();
    this.notifyTray();
    log.info('Cleared workspace');
  }

  /**
   * 7 days after the last edit -- except that a workspace holding any `media` never auto-expires
   * (spec--pdfmarkuptool-image-markup 4.2, SR C13): its photos may exist nowhere else. Its PDF is kept with it
   * (PdfCacheService never ages out the workspace's own blob).
   */
  async isExpired(workspace: LocalWorkspace): Promise<boolean> {
    if (Date.now() - workspace.lastModifiedAt <= this.EXPIRATION_MS) return false;
    return (await this.mediaCount()) === 0;
  }

  /**
   * Distinct photos across every markup of the workspace and its unplaced tray. Distinct ASSETS (I8b): a photo placed
   * from the tray keeps its stored entry (the visible tray is derived), so it is on a markup and in the tray at once.
   */
  async mediaCount(): Promise<number> {
    const assets = placedAssets(await this.getAllMarkups() as Array<{ media?: Record<string, { asset: string }> }>);
    for (const e of (await this.getWorkspace())?.tray ?? []) assets.add(e.asset);
    return assets.size;
  }

  // ==================== Tray (image markup slice I8) ====================

  async getTray(): Promise<TrayEntry[]> {
    return orderTray((await this.getWorkspace())?.tray ?? []);
  }

  /**
   * The tray's read-modify-write, in ONE transaction on the workspace record (a markup write's touch of the same
   * record cannot interleave and put back a stale tray). With no workspace nothing is written and [] is returned.
   */
  async updateTray(fn: (tray: TrayEntry[]) => TrayEntry[]): Promise<TrayEntry[]> {
    if (!this.db) await this.init();

    const tx = this.db!.transaction('workspace', 'readwrite');
    const existing = await tx.store.get('current');
    if (!existing) {
      await tx.done;
      return [];
    }
    const tray = orderTray(fn(orderTray(existing.tray ?? [])));
    await tx.store.put({ ...existing, tray, lastModifiedAt: Date.now() }, 'current');
    await tx.done;
    this.notifyTray();
    return tray;
  }

  subscribeTray(cb: () => void): () => void {
    this.traySubscribers.add(cb);
    return () => {
      this.traySubscribers.delete(cb);
    };
  }

  private notifyTray(): void {
    this.traySubscribers.forEach((cb) => {
      try {
        cb();
      } catch (error) {
        log.error('Error in tray subscriber:', error);
      }
    });
  }

  // ==================== Markups ====================

  async getAllMarkups(): Promise<Markup[]> {
    if (!this.db) await this.init();

    try {
      return await this.db!.getAll('markups');
    } catch (error) {
      log.error('Error getting all markups:', error);
      return [];
    }
  }

  async getMarkupsByPage(pageNumber: number): Promise<Markup[]> {
    if (!this.db) await this.init();

    try {
      return await this.db!.getAllFromIndex('markups', 'byPage', pageNumber);
    } catch (error) {
      log.error(`Error getting markups for page ${pageNumber}:`, error);
      return [];
    }
  }

  async getMarkup(id: string): Promise<Markup | null> {
    if (!this.db) await this.init();

    try {
      const markup = await this.db!.get('markups', id);
      return markup || null;
    } catch (error) {
      log.error(`Error getting markup ${id}:`, error);
      return null;
    }
  }

  async addMarkup(markup: Markup): Promise<void> {
    if (!this.db) await this.init();

    await this.db!.put('markups', markup);
    await this.touchWorkspace();
    this.notify();
    log.info(`Added markup ${markup.id}`);
  }

  /**
   * Put a markup back by id (undo of a delete, redo of a create, import).
   * Unlike addMarkup (a fresh create at rev 1), a restore is a write to a
   * markup that already had a life: rev = max(snapshot, any stored copy) + 1,
   * so rev never repeats across delete/restore cycles. Returns what was stored.
   */
  async restoreMarkup(markup: Markup): Promise<Markup> {
    if (!this.db) await this.init();

    const stored = await this.getMarkup(markup.id);
    const restored = restoredMarkup(markup, stored?.rev);
    await this.db!.put('markups', restored);
    await this.touchWorkspace();
    this.notify();
    log.info(`Restored markup ${markup.id}`);
    return restored;
  }

  async updateMarkup(id: string, updates: Partial<Markup>): Promise<void> {
    if (!this.db) await this.init();

    const existing = await this.getMarkup(id);
    if (!existing) {
      throw new Error(`Markup ${id} not found`);
    }

    // One write: patch, then rev = existing + 1 and a monotonic updatedAt.
    const updated: Markup = applyMarkupWrite(existing, updates);

    await this.db!.put('markups', updated);
    await this.touchWorkspace();
    this.notify();
    log.info(`Updated markup ${id}`);
  }

  async deleteMarkup(id: string): Promise<void> {
    if (!this.db) await this.init();

    await this.db!.delete('markups', id);
    await this.touchWorkspace();
    this.notify();
    log.info(`Deleted markup ${id}`);
  }

  async deleteMarkupsByPage(pageNumber: number): Promise<void> {
    if (!this.db) await this.init();

    const markups = await this.getMarkupsByPage(pageNumber);
    await Promise.all(markups.map((m) => this.db!.delete('markups', m.id)));
    await this.touchWorkspace();
    this.notify();
    log.info(`Deleted ${markups.length} markups from page ${pageNumber}`);
  }

  // ==================== Page Scales ====================

  async getAllPageScales(): Promise<PageScale[]> {
    if (!this.db) await this.init();

    try {
      return await this.db!.getAll('pageScales');
    } catch (error) {
      log.error('Error getting all page scales:', error);
      return [];
    }
  }

  async getPageScale(pageNumber: number): Promise<PageScale | null> {
    if (!this.db) await this.init();

    try {
      const scale = await this.db!.get('pageScales', pageNumber);
      return scale || null;
    } catch (error) {
      log.error(`Error getting scale for page ${pageNumber}:`, error);
      return null;
    }
  }

  async setPageScale(pageNumber: number, scale: PageScale): Promise<void> {
    if (!this.db) await this.init();

    await this.db!.put('pageScales', { ...scale, pageNumber });
    await this.touchWorkspace();
    log.info(`Set scale for page ${pageNumber}`);
  }

  async deletePageScale(pageNumber: number): Promise<void> {
    if (!this.db) await this.init();

    await this.db!.delete('pageScales', pageNumber);
    await this.touchWorkspace();
    log.info(`Deleted scale for page ${pageNumber}`);
  }

  // ==================== Helpers ====================

  private async touchWorkspace(): Promise<void> {
    // Read and write in one transaction: a stale copy must never overwrite a concurrent tray write.
    const tx = this.db!.transaction('workspace', 'readwrite');
    const workspace = await tx.store.get('current');
    if (workspace) await tx.store.put({ ...workspace, lastModifiedAt: Date.now() }, 'current');
    await tx.done;
    if (workspace) {
      // Keep the PDF blob TTL in sync with the workspace TTL so the two cannot
      // expire on different clocks. refreshCachedAt() silently no-ops when the
      // entry is absent and swallows IDB errors, so this cannot break edits.
      await pdfCache.refreshCachedAt(workspace.pdfBlobKey);
    }
  }
}

export const localWorkspaceService = new LocalWorkspaceService();

/** The local workspace's tray (the {@link TrayStore} seam; the cloud tray, a document field, is slice I9's adapter). */
export const localTrayStore: TrayStore = {
  load: () => localWorkspaceService.getTray(),
  update: (fn) => localWorkspaceService.updateTray(fn),
  subscribe: (listener) => localWorkspaceService.subscribeTray(listener),
};

// The live workspace's PDF is protected from the PDF cache's eviction and age-out -- as long as the workspace
// itself has not expired, so the two share ONE expiry rule (isExpired: photos keep both).
setPdfCacheProtectedKeyResolver(async () => {
  const workspace = await localWorkspaceService.getWorkspace();
  if (!workspace || (await localWorkspaceService.isExpired(workspace))) return null;
  return workspace.pdfBlobKey;
});
